import { observer } from "mobx-react";
import { ReactNode } from "react";
import { Alert } from "@/components-v2/Alert";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { AutoSizer } from "@/components-v2/AutoSizer";
import { Box, Card, Grid, VBox } from "@/components-v2/Box";
import { Button, IconButton, LinkButton, ToggleButton } from "@/components-v2/Button";
import { MCheckbox } from "@/components-v2/Checkbox";
import { DataCard } from "@/components-v2/DataCard";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { MDateInput } from "@/components-v2/DateInput";
import { Dialog } from "@/components-v2/Dialog";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { EmptyState } from "@/components-v2/EmptyState";
import { Icon } from "@/components-v2/Icon";
import { InfoButton } from "@/components-v2/InfoButton";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { navigate, showAlert } from "@/events/eventsDomain";
import { adminOptionProgram } from "@/utils/navigation";
import { FilePreviewDialog } from "@/components-v2/FilePreview";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MSelect, Select } from "@/components-v2/Select";
import {
  Sparkline,
  SparklineAnnotation,
  SparklineComponent,
  SparklineTick,
} from "@/components-v2/Sparkline";
import { Table } from "@/components-v2/Table";
import { Tabs } from "@/components-v2/Tabs";
import { Tag, TagColor } from "@/components-v2/Tag";
import { TextWithTooltip } from "@/components-v2/TextWithTooltip";
import { TMessageKey } from "@/i18n/types.gen";
import { MTextArea } from "@/components-v2/TextArea";
import { MTextInput } from "@/components-v2/TextInput";
import { Markdown } from "@/text/Markdown";
import { MarkdownEditor } from "@/text/MarkdownEditor";
import { ShareValuationInformation } from "@/common/common";
import { StringFieldModel } from "@/common/commonForms";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { lookupShareTypeName, ShareType } from "@/common/share";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import { TabContent } from "@/shareholderRegister/shareholders/shareholderDetailsComponents";
import { RegisterExerciseDialog } from "./exerciseComponents";
import {
  useDispatch,
  useRootStore,
  useShareholdersStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import { ActorType } from "@/types/SelectActors";
import { norway } from "@/utils/countries";
import { plusMonths, today, toLocalDateISOString } from "@/utils/Dates";
import {
  closeReverseExerciseDialog,
  ExerciseListRow,
  exerciseListRows,
  exerciseRowActions,
  exerciseStageTag,
  exerciseStageCount,
  exerciseTabLabelKeys,
  holderExerciseRequests,
  holderRequestButton,
  OptionTranche,
  HolderExerciseRequestScope,
  exerciseRequestCandidates,
  startExerciseRequest,
  ExerciseTab,
  filterExerciseRows,
  setExercisesPlan,
  setExercisesTab,
  ReverseExerciseState,
  submitReverseExercise,
  addMilestone,
  addVestingDateRow,
  allTranchesWithPlan,
  accelerationComplete,
  AccelerationGrantOption,
  AccelerationState,
  closeAccelerationDialog,
  selectedAccelerationOption,
  setAccelerationGrant,
  setAccelerationTab,
  stageAccelerationFiles,
  removeStagedAccelerationFile,
  submitAcceleration,
  cancellationComplete,
  CancellationGrantOption,
  CancellationState,
  closeCancellationDialog,
  selectedCancellationOption,
  setCancellationGrant,
  setCancellationTab,
  stageCancellationFiles,
  removeStagedCancellationFile,
  stageCreatePlanFiles,
  removeStagedCreatePlanFile,
  submitCancellation,
  closeCreatePlanDialog,
  closeEditPlanDialog,
  closeAttachmentPreview,
  closeEditGrantDialog,
  closeDeleteGrantDialog,
  closeEditVestingDialog,
  closeRegisterGrantDialog,
  CreatePlanState,
  DeleteGrantState,
  deletePlanAttachment,
  deleteTrancheAttachment,
  DurationFields,
  EditGrantState,
  EditVestingState,
  findTranche,
  FullyDilutedBasis,
  fullyDilutedBasisFor,
  GrantForm,
  grantActionItems,
  grantStepEnabled,
  GrantWizard,
  groupTranchesByProgram,
  HolderMilestoneGroup,
  holderTranches,
  isPerformanceVesting,
  isSpecificDatesVesting,
  performanceMilestonesComplete,
  planAttachments,
  planAttachmentUrl,
  openAttachmentPreview,
  openCreatePlanDialog,
  openEditPlanDialog,
  optionAttachmentUploadConfig,
  OptionGrantDetails,
  OptionPlan,
  optionsAbovePoolAfterGrant,
  optionsPageData,
  prepHolderHistory,
  programLabel,
  prepHolderMilestones,
  prepHolderOptionCards,
  prepHolderOptionValues,
  prepVestingTemplateSummary,
  selectGrantPlan,
  startEditPlanVesting,
  closeEditPlanVestingDialog,
  submitEditPlanVesting,
  EditPlanVestingState,
  prepHolderVestingSeries,
  OptionValuePoint,
  OptionsTableRow,
  OptionPoolChange,
  EditPlanState,
  prepProgramPoolRows,
  ProgramPoolRow,
  RegisterGrantState,
  removeMilestone,
  removeVestingDateRow,
  setShowAllVestingEntries,
  setVestingFrequency,
  setWizardStep,
  ShareholderOptionsView,
  startAddNewHolder,
  startEditGrant,
  startEditVesting,
  startGrantForHolder,
  startGrantForPortfolioActor,
  startGrantForQuickAddActor,
  submitCreatePlan,
  submitDeleteGrant,
  submitEditGrant,
  submitEditVesting,
  submitEditPlan,
  submitGrantStep,
  submitHolderStep,
  submitRegisterGrant,
  trancheAttachmentUrl,
  trancheNumbers,
  TrancheWithPlan,
  toggleAddMilestone,
  unallocatedMilestoneOptions,
  unscheduledOptions,
  uploadPlanAttachments,
  uploadTrancheAttachments,
  vestingCliffDate,
  VestingChartSeries,
  vestingChartPoints,
  vestingChartSummary,
  vestingExerciseMarkers,
  vestingProgressPoints,
  VestingForm,
  vestingSchedule,
  vestingTypeLabel,
  wizardHolderName,
  milestoneScopeLabelKeys,
  awaitingShares,
  addExerciseWindow,
  removeExerciseWindow,
  exerciseWindowStatus,
  exerciseWindowsTooltip,
  activeExerciseWindows,
  exerciseRequestTimingDescription,
  exerciseRequestTimingLabel,
  planRequestPolicy,
  chartExerciseWindows,
  ExerciseWindow,
  ExerciseWindowRow,
  ExerciseWindowStatus,
  PlanDialog,
} from "./optionsDomain";
import {
  holderForShareholder,
  holderPortfolioReferences,
  InstrumentHolder,
  loadPortfolioActors,
  NewHolderForm,
  quickAddSuggestions,
  setHolderNationality,
} from "./instrumentHolders";
import {
  DeleteExerciseRequestDialog,
  AdminExerciseRequestsCard,
  ExerciseRequestDialogs,
  ExerciseRequestFormDialog,
  HolderExerciseRequestsCard,
} from "./exerciseRequestComponents";

export const RegisterGrantDialog = observer(function RegisterGrantDialog({
  registerGrant,
  holders,
  plans,
}: {
  registerGrant: RegisterGrantState;
  holders: InstrumentHolder[];
  plans: OptionPlan[];
}) {
  if (registerGrant.view === "wizard") {
    return <GrantWizardDialog wizard={registerGrant} />;
  }
  return <QuickAddGrantDialog holders={holders} plans={plans} />;
});

const QuickAddGrantDialog = observer(function QuickAddGrantDialog({
  holders,
  plans,
}: {
  holders: InstrumentHolder[];
  plans: OptionPlan[];
}) {
  const { t, lang } = useTranslation();
  const { activeShareTypes, shareholders } = useShareholdersStore();
  const { dialogStore, optionsState } = useRootStore();
  const { orgNumber } = useUserStore();
  const dispatch = useDispatch();
  const suggestions = quickAddSuggestions(optionsState?.quickAddActors ?? [], holders, orgNumber);

  return (
    <Dialog
      title={{ key: "options.register-new-grant" }}
      onCancel={[closeRegisterGrantDialog()]}
      onSubmit={[]}
      isSubmitDisabled
      submitLabel={{ key: "save" }}
    >
      <VBox gap={"lg"} marginY={"sm"}>
        <Box>
          <DropdownMenu
            button={{ type: "button", variant: "secondary", label: t("options.add-holder") }}
            items={[
              {
                type: "item",
                icon: "user-plus",
                label: { key: "options.add-new" },
                onSelect: [startAddNewHolder(lang, plans, activeShareTypes, shareholders)],
              },
              {
                type: "item",
                icon: "suitcaseSimple",
                label: { key: "options.add-from-portfolio" },
                onSelect: () =>
                  dialogStore.openActorSelectorDialog({
                    title: { key: "options.add-holder" },
                    organisationNumber: orgNumber,
                    tabs: [ActorType.SHAREHOLDER, ActorType.BOARD_MEMBER, ActorType.CEO],
                    excludeActors: holderPortfolioReferences(holders),
                    onLoad: (type, onSuccess, onFailure) =>
                      dispatch(loadPortfolioActors(orgNumber, type, onSuccess, onFailure)),
                    onSubmit: (actors, onSuccess) => {
                      dispatch([
                        startGrantForPortfolioActor(actors[0], lang, plans, activeShareTypes),
                      ]);
                      onSuccess();
                    },
                    isMultiSelect: false,
                  }),
              },
            ]}
          />
        </Box>
        <Box wrap gap={"sm"}>
          {suggestions.map((suggestion) => (
            <ToggleButton
              key={suggestion.actor.socialSecurityNumber ?? suggestion.actor.name}
              icon={"plus"}
              variant={"small"}
              label={suggestion.actor.name}
              onClick={[
                startGrantForQuickAddActor(suggestion, orgNumber, lang, plans, activeShareTypes),
              ]}
            />
          ))}
        </Box>
      </VBox>
    </Dialog>
  );
});

const GrantWizardDialog = observer(function GrantWizardDialog({ wizard }: { wizard: GrantWizard }) {
  const { orgNumber } = useUserStore();
  const { step, holderForm } = wizard;
  const holderName = wizardHolderName(wizard);
  const dispatch = useDispatch();

  return (
    <Dialog
      title={
        holderName === undefined
          ? { key: "options.register-new-grant" }
          : { key: "options.register-new-grant-for", params: { name: holderName } }
      }
      onCancel={[closeRegisterGrantDialog()]}
      onSubmit={() =>
        dispatch(
          step === "holder-details" && holderForm
            ? submitHolderStep(holderForm)
            : step === "grant"
              ? submitGrantStep(wizard.grantForm)
              : submitRegisterGrant(orgNumber, wizard),
        )
      }
      submitResult={wizard.submitResult}
      submitLabel={step === "vesting-schedule" ? { key: "save" } : { key: "next" }}
      isSubmitDisabled={
        step === "vesting-schedule" &&
        isPerformanceVesting(wizard.vestingForm) &&
        !performanceMilestonesComplete(wizard.grantForm, wizard.vestingForm)
      }
      subHeaderElement={
        <Tabs
          paddingX={"md"}
          tabs={[
            holderForm !== undefined && {
              id: "holder-details",
              label: { key: "options.holder-details" } as const,
              isActive: step === "holder-details",
              onSelect: [setWizardStep("holder-details")],
            },
            {
              id: "grant",
              label: { key: "options.grant" } as const,
              isActive: step === "grant",
              onSelect: [setWizardStep("grant")],
              disabled: !grantStepEnabled(wizard),
            },
            {
              id: "vesting-schedule",
              label: { key: "instrument.vesting-schedule" } as const,
              isActive: step === "vesting-schedule",
              onSelect: [setWizardStep("vesting-schedule")],
              disabled: !wizard.grantStepCompleted,
            },
          ].filter((tab) => tab !== false)}
        />
      }
    >
      {step === "holder-details" && holderForm && <HolderDetailsStep form={holderForm} />}
      {step === "grant" && <GrantStep form={wizard.grantForm} />}
      {step === "vesting-schedule" && (
        <VestingStep
          grantForm={wizard.grantForm}
          vestingForm={wizard.vestingForm}
          showAll={wizard.showAllVestingEntries ?? false}
        />
      )}
    </Dialog>
  );
});

const VestingStep = observer(function VestingStep({
  grantForm,
  vestingForm,
  showAll,
}: {
  grantForm: GrantForm;
  vestingForm: VestingForm;
  showAll: boolean;
}) {
  const { t, tf, f } = useTranslation();
  const performance = isPerformanceVesting(vestingForm);
  const specificDates = isSpecificDatesVesting(vestingForm);
  const schedule = vestingSchedule(grantForm, vestingForm);
  const visibleEntries = showAll ? schedule : schedule.slice(0, 4);
  const grantDate = grantForm.grantDate.validatedValue;
  const planName = grantForm.plan.value?.name;

  return (
    <VBox gap={"xl"} marginY={"md"}>
      <Grid cols={1} colsMd={2} gap={"lg"}>
        <MSelect label={t("options.vesting-type")} model={vestingForm.vestingType} fullWidth />
        {performance ? (
          grantForm.expirationDate.validatedValue !== undefined && (
            <MDateInput label={t("options.expiration")} model={grantForm.expirationDate} disabled />
          )
        ) : (
          <>
            <DurationInput label={t("options.period")} duration={vestingForm.period} />
            <MSelect
              label={t("options.frequency")}
              model={vestingForm.frequency}
              onSelect={(choice) => [setVestingFrequency(choice)]}
              fullWidth
            />
            <DurationInput label={t("options.cliff")} duration={vestingForm.cliff} />
          </>
        )}
      </Grid>
      {performance ? (
        <MilestonesEditor grantForm={grantForm} vestingForm={vestingForm} />
      ) : specificDates ? (
        <SpecificDatesEditor grantForm={grantForm} vestingForm={vestingForm} />
      ) : (
        schedule.length > 0 &&
        grantDate !== undefined && (
          <VBox gap={"sm"}>
            <strong>{t("options.vesting-dates-heading")}</strong>
            <Table withRowBorders>
              <thead>
                <tr>
                  <th align={"left"}>{t("instrument.vesting-date")}</th>
                  <th align={"left"}>{t("options.options-header")}</th>
                  <th align={"left"}>{t("options.percent-of-total")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleEntries.map((entry) => (
                  <tr key={entry.date}>
                    <td>{f.formatDate(entry.date)}</td>
                    <td>{f.formatNumber(entry.options)}</td>
                    <td>
                      {f.formatPercent(entry.fractionOfTotal, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {schedule.length > 4 && (
              <Box>
                {showAll ? (
                  <LinkButton
                    icon={"minus-square"}
                    label={t("options.show-less")}
                    onClick={[setShowAllVestingEntries(false)]}
                  />
                ) : (
                  <LinkButton
                    icon={"plus-square"}
                    label={tf({ key: "options.show-all", params: { count: schedule.length } })}
                    onClick={[setShowAllVestingEntries(true)]}
                  />
                )}
              </Box>
            )}
          </VBox>
        )
      )}
      {!performance && schedule.length > 0 && grantDate !== undefined && (
        <VBox gap={"sm"}>
          <div>{t("options.preview")}</div>
          <VestingChart
            series={[
              {
                label: planName ?? "",
                startDate: grantDate,
                entries: schedule,
                cliffDate: vestingCliffDate(grantForm, vestingForm),
              },
            ]}
          />
        </VBox>
      )}
    </VBox>
  );
});

const DurationInput = observer(function DurationInput({
  label,
  duration,
}: {
  label: string;
  duration: DurationFields;
}) {
  return (
    <VBox gap={0}>
      <label className={"block mb-1"}>{label}</label>
      <Box gap={"md"} alignItems={"start"}>
        <Box className={"w-28"}>
          <MTextInput model={duration.amount} align={"right"} />
        </Box>
        <MSelect model={duration.unit} fullWidth />
      </Box>
    </VBox>
  );
});

const SpecificDatesEditor = observer(function SpecificDatesEditor({
  grantForm,
  vestingForm,
}: {
  grantForm: GrantForm;
  vestingForm: VestingForm;
}) {
  const { t, tf, f } = useTranslation();
  const totalOptions = grantForm.numberOfOptions.toNumber();
  const unscheduled = unscheduledOptions(grantForm, vestingForm);

  return (
    <VBox gap={"sm"}>
      <strong>{t("options.specify-vesting-dates")}</strong>
      <Table withRowBorders>
        <thead>
          <tr>
            <th>{t("instrument.vesting-date")}</th>
            <th>{t("options.options-header")}</th>
            <th>{t("options.percent-of-total")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {vestingForm.specificDates.map((row) => {
            const amount = row.options.toNumber();
            return (
              <tr key={row.id}>
                <td>
                  <MDateInput model={row.date} />
                </td>
                <td>
                  <MTextInput model={row.options} />
                </td>
                <td>
                  {totalOptions !== undefined && totalOptions > 0 && amount !== undefined
                    ? f.formatPercent(amount / totalOptions, {
                        minimumFractionDigits: 3,
                        maximumFractionDigits: 3,
                      })
                    : undefined}
                </td>
                <td>
                  <IconButton
                    icon={"trash"}
                    color={"error"}
                    tooltip={t("delete")}
                    disabled={vestingForm.specificDates.length === 1}
                    onClick={[removeVestingDateRow(row.id)]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <Box alignItems={"center"} gap={"xl"}>
        <LinkButton icon={"plus"} label={t("options.add-date")} onClick={[addVestingDateRow()]} />
        {unscheduled !== 0 && (
          <span className={"color-error"}>
            {tf({
              key: "options.to-schedule",
              params: { count: { type: "number", value: unscheduled } },
            })}
          </span>
        )}
      </Box>
    </VBox>
  );
});

const MilestonesEditor = observer(function MilestonesEditor({
  grantForm,
  vestingForm,
}: {
  grantForm: GrantForm;
  vestingForm: VestingForm;
}) {
  const { t, tf, f } = useTranslation();
  const totalOptions = grantForm.numberOfOptions.toNumber();
  const unallocated = unallocatedMilestoneOptions(grantForm, vestingForm);
  const showAdd = vestingForm.showAddMilestone;
  const unallocatedNote =
    unallocated !== 0 && vestingForm.milestones.length > 0 ? (
      <span className={"color-error"}>
        {tf({
          key: "options.unallocated",
          params: { count: { type: "number", value: unallocated } },
        })}
      </span>
    ) : undefined;

  return (
    <VBox gap={"lg"}>
      {vestingForm.milestones.length > 0 && (
        <Table withRowBorders>
          <thead>
            <tr>
              <th align={"left"}>{t("options.milestones")}</th>
              <th align={"left"}>{t("options.milestone-vesting")}</th>
              <th align={"left"}>{t("options.percent-of-total")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {vestingForm.milestones.map((row) => {
              const amount = row.options.toNumber();
              return (
                <tr key={row.id}>
                  <td>
                    <Box alignItems={"center"} gap={"md"}>
                      <Icon
                        name={row.scope === "COMPANY_WIDE" ? "buildings" : "user-square"}
                        size={"lg"}
                      />
                      <VBox gap={0}>
                        <div>{row.title}</div>
                        {row.criterion !== undefined && row.criterion !== "" && (
                          <div className={"subtle"}>{row.criterion}</div>
                        )}
                      </VBox>
                    </Box>
                  </td>
                  <td>
                    <MTextInput model={row.options} align={"right"} />
                  </td>
                  <td>
                    {totalOptions !== undefined && totalOptions > 0 && amount !== undefined
                      ? f.formatPercent(amount / totalOptions, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : undefined}
                  </td>
                  <td>
                    <IconButton
                      icon={"trash"}
                      color={"error"}
                      tooltip={t("delete")}
                      onClick={[removeMilestone(row.id)]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {showAdd ? (
        <Card gap={"md"}>
          <strong>{t("options.add-milestone")}</strong>
          <MRadioButtonGroup gap={"xl"} model={vestingForm.addMilestoneScope} horizontal />
          <MTextInput label={t("options.milestone-title")} model={vestingForm.addMilestoneTitle} />
          <MTextArea
            label={t("options.milestone-criterion")}
            model={vestingForm.addMilestoneCriterion}
          />
          <Box alignItems={"center"} gap={"md"}>
            <Button
              variant={"primary"}
              label={t("options.add-milestone")}
              onClick={[addMilestone()]}
            />
            <Button variant={"secondary"} label={t("cancel")} onClick={[toggleAddMilestone()]} />
            {unallocatedNote}
          </Box>
        </Card>
      ) : (
        <Box alignItems={"center"} gap={"xl"}>
          <LinkButton
            icon={"plus"}
            label={t("options.add-milestone")}
            onClick={[toggleAddMilestone()]}
          />
          {unallocatedNote}
        </Box>
      )}
    </VBox>
  );
});

const HolderMilestones = observer(function HolderMilestones({
  group,
}: {
  group: HolderMilestoneGroup;
}) {
  const { t, f } = useTranslation();
  return (
    <VBox gap={"sm"}>
      <Table withRowBorders>
        <thead>
          <tr>
            <th align={"left"}>{t("options.milestones")}</th>
            <th align={"left"}>{t("options.milestone-vesting")}</th>
            <th align={"left"}>{t("type")}</th>
            <th align={"left"}>{t("options.percent-of-total")}</th>
          </tr>
        </thead>
        <tbody>
          {group.milestones.map((milestone, index) => (
            <tr key={index}>
              <td>
                <Box alignItems={"center"} gap={"md"}>
                  <Icon
                    name={milestone.scope === "COMPANY_WIDE" ? "buildings" : "user-square"}
                    size={"lg"}
                  />
                  <VBox gap={0}>
                    <span>{milestone.title}</span>
                    <div className={"subtle"}>
                      {milestone.criterion !== undefined && milestone.criterion !== "" && (
                        <span>{milestone.criterion}</span>
                      )}
                    </div>
                  </VBox>
                </Box>
              </td>
              <td>{f.formatNumber(milestone.optionsAmount)}</td>
              <td>{t(milestoneScopeLabelKeys[milestone.scope])}</td>
              <td>
                {f.formatPercent(milestone.fractionOfTotal, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </VBox>
  );
});

const VESTING_CHART = {
  height: 300,
  margin: { top: 36, left: 64, bottom: 28, right: 16 },
  summaryRight: 48,
};

const EXERCISE_LANE = { height: 44, offsetY: -24, thickness: 8 };

const EXERCISE_WINDOW_COLORS: Record<ExerciseWindowStatus, string> = {
  closed: "var(--color-gray)",
  open: "var(--color-accent1)",
  upcoming: "var(--color-gray-hard)",
};

const SERIES_COLORS = [
  "var(--color-primary)",
  "var(--color-secondary)",
  "var(--color-primary-dark)",
  "var(--color-secondary-dark)",
];

const seriesColor = (index: number): string => SERIES_COLORS[index % SERIES_COLORS.length];

const niceCeil = (value: number): number => {
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((c) => c >= value) ?? value;
};

// The highest point the cumulative curve reaches — not just its final value,
// since a cancellation can make the curve dip and then rise again.
const peakCumulative = (entries: VestingChartSeries["entries"]): number => {
  let cumulative = 0;
  let peak = 0;
  for (const entry of entries) {
    cumulative += entry.options;
    peak = Math.max(peak, cumulative);
  }
  return peak;
};

type VestingPoint = {
  date: number;
  cumulative: number;
  exercised?: number;
  tooltip?: TMessageParam;
};

// Vertical dashed rules marking grant events (accelerations, cancellations).
// The caption is drawn once for the group so several close-together events don't
// stack the same word on top of itself; the remaining dates render as bare lines.
const eventRuleComponents = (
  dates: string[],
  xMin: number,
  xMax: number,
  label: string,
  labelDy: number,
): SparklineComponent<VestingPoint>[] =>
  [...new Set(dates.map((date) => new Date(date).getTime()))]
    .filter((ms) => ms >= xMin && ms <= xMax)
    .sort((a, b) => a - b)
    .map((ms, index) => ({
      type: "x-rule" as const,
      value: ms,
      dashed: true,
      opacity: 0.5,
      labelAnchor: "middle" as const,
      label: index === 0 ? label : undefined,
      labelDy,
    }));

type ChartLegendSwatch = "line" | "box" | "bar";

const ChartLegendItem = ({
  swatch,
  color,
  opacity = 1,
  bordered,
  label,
}: {
  swatch: ChartLegendSwatch;
  color: string;
  opacity?: number;
  bordered?: boolean;
  label: string;
}) => (
  <Box alignItems={"center"} gap={"xs"}>
    <svg width={18} height={12} role={"presentation"}>
      {swatch === "box" ? (
        <rect
          x={1}
          y={1}
          width={16}
          height={10}
          rx={2}
          fill={color}
          fillOpacity={opacity}
          stroke={bordered ? "var(--color-gray-hard)" : undefined}
          strokeOpacity={0.4}
        />
      ) : (
        <rect
          x={0}
          y={swatch === "line" ? 4.5 : 3}
          width={18}
          height={swatch === "line" ? 3 : 6}
          rx={swatch === "line" ? 1.5 : 3}
          fill={color}
        />
      )}
    </svg>
    <small className={"subtle"}>{label}</small>
  </Box>
);

const EXERCISE_WINDOW_LEGEND: Record<ExerciseWindowStatus, TMessageKey> = {
  open: "options.vesting-legend-window-open",
  upcoming: "options.vesting-legend-window-upcoming",
  closed: "options.vesting-legend-window-closed",
};

const yearTicksBetween = (xMin: number, xMax: number, formatYear: (date: Date) => string) => {
  const firstYear = new Date(xMin).getUTCFullYear();
  const lastYear = new Date(xMax).getUTCFullYear();
  return Array.from({ length: lastYear - firstYear + 1 }, (_, i) => Date.UTC(firstYear + i, 0, 1))
    .filter((ms) => ms >= xMin && ms <= xMax)
    .map((ms): SparklineTick => ({ value: ms, label: formatYear(new Date(ms)) }));
};

const VestingChart = observer(function VestingChart({
  series,
  today,
  exerciseWindows = [],
}: {
  series: VestingChartSeries[];
  today?: string;
  exerciseWindows?: ExerciseWindow[];
}) {
  const { t, tf, f } = useTranslation();
  if (series.length === 0) {
    return null;
  }
  const showProgress = today !== undefined;
  const chartStart = series.map((s) => s.startDate).sort()[0];
  const windowBars =
    today === undefined ? [] : chartExerciseWindows(exerciseWindows, chartStart, today);
  const laneHeight = windowBars.length > 0 ? EXERCISE_LANE.height : 0;
  const xMin = new Date(chartStart).getTime();
  const xMax = Math.max(
    ...series.map((s) => plusMonths(s.entries[s.entries.length - 1].date, 6).getTime()),
    ...windowBars.map((bar) => plusMonths(bar.toDate, 1).getTime()),
  );
  const endDate = toLocalDateISOString(new Date(xMax));
  const yMax = niceCeil(Math.max(...series.map((s) => peakCumulative(s.entries))));

  const lines = series.map((s, index) => {
    let cumulative = 0;
    const points: VestingPoint[] = [{ date: new Date(s.startDate).getTime(), cumulative: 0 }];
    for (const entry of s.entries) {
      cumulative += entry.options;
      points.push({ date: new Date(entry.date).getTime(), cumulative });
    }
    points.push({ date: xMax, cumulative });
    const cliffPoint =
      s.cliffDate !== undefined && s.entries.length > 0
        ? { date: new Date(s.entries[0].date).getTime(), cumulative: s.entries[0].options }
        : undefined;
    const progress = vestingProgressPoints(s, endDate).map(
      (point): VestingPoint => ({
        date: new Date(point.date).getTime(),
        cumulative: point.vested,
        exercised: point.exercised,
      }),
    );
    return {
      ...s,
      index,
      points,
      cliffPoint,
      progress,
      markers: vestingExerciseMarkers(s),
      summary: vestingChartSummary(s),
    };
  });
  const hasExercises = lines.some((line) => line.markers.length > 0);
  const summary = showProgress && lines.length === 1 ? lines[0].summary : undefined;

  const chartPoints = vestingChartPoints(series);
  const hoverPoints: VestingPoint[] = chartPoints.map((point) => {
    const date = new Date(point.date).getTime();
    const exercised =
      lines.length === 1 ? lines[0].progress.find((p) => p.date === date)?.exercised : undefined;
    return {
      date,
      cumulative: Math.max(...point.vested.map((vested) => vested.cumulative)),
      tooltip: {
        type: "list",
        value: [
          { type: "date", value: point.date },
          ...point.vested.map(
            (vested): TMessage =>
              series.length > 1
                ? {
                    key: "options.vesting-tooltip-series",
                    params: {
                      label: vested.label,
                      vested: { type: "number", value: vested.cumulative },
                    },
                  }
                : {
                    key: "options.vesting-tooltip",
                    params: { vested: { type: "number", value: vested.cumulative } },
                  },
          ),
          ...(showProgress && hasExercises && exercised !== undefined
            ? point.vested.map(
                (vested): TMessage => ({
                  key: "options.vesting-tooltip-exercisable",
                  params: { number: { type: "number", value: vested.cumulative - exercised } },
                }),
              )
            : []),
        ],
        separator: "\n",
      },
    };
  });

  const yTicks: SparklineTick[] = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    value: fraction * yMax,
    label: f.formatNumber(fraction * yMax),
  }));
  const yearTicks = yearTicksBetween(xMin, xMax, f.formatYear);
  const xTicks: SparklineTick[] =
    yearTicks.length > 0 ? yearTicks : [{ value: xMin, label: f.formatYearMonth(new Date(xMin)) }];
  const todayMs = today === undefined ? undefined : new Date(today).getTime();
  const showToday = todayMs !== undefined && todayMs >= xMin && todayMs <= xMax;

  const annotations: SparklineAnnotation[] = [
    ...(showToday && todayMs !== undefined
      ? [
          {
            key: "today",
            x: todayMs,
            y: yMax,
            dy: -laneHeight,
            variant: "dark" as const,
            content: t("options.today"),
          },
        ]
      : []),
    ...lines.flatMap((line) =>
      line.markers.map(
        (marker): SparklineAnnotation => ({
          key: `exercise-${line.index}-${marker.date}`,
          x: new Date(marker.date).getTime(),
          y: marker.vested,
          content: tf({
            key: "options.vesting-exercised-marker",
            params: {
              number: { type: "number", value: marker.count },
              date: { type: "date", value: marker.date },
            },
          }),
        }),
      ),
    ),
    ...(summary === undefined
      ? []
      : [
          {
            key: "summary",
            x: new Date(summary.date).getTime(),
            y: summary.vested,
            placement: "right" as const,
            content: (
              <VBox gap={0}>
                <strong className={"color-primary"}>
                  {tf({
                    key: "options.vesting-summary-vested",
                    params: { number: { type: "number", value: summary.vested } },
                  })}
                </strong>
                <span className={"color-primary"}>
                  {tf({
                    key: "options.vesting-summary-remaining",
                    params: { number: { type: "number", value: summary.remaining } },
                  })}
                </span>
              </VBox>
            ),
          },
        ]),
  ];

  const components: SparklineComponent<VestingPoint>[] = [
    { type: "plot-background" },
    {
      type: "y-axis",
      ticks: yTicks,
      label: showProgress ? t("options.vesting-axis-label") : undefined,
    },
    { type: "x-ticks", ticks: xTicks },
    ...yearTicks.map((tick) => ({
      type: "x-rule" as const,
      value: tick.value,
      dashed: true,
      opacity: 0.15,
    })),
    ...(showProgress
      ? lines.flatMap((line) => [
          ...(line.markers.length > 0
            ? [
                {
                  type: "band" as const,
                  data: line.progress,
                  y0: () => 0,
                  y1: (d: VestingPoint) => d.exercised ?? 0,
                  color: "var(--color-bg-default)",
                  opacity: 0.85,
                  curve: "step" as const,
                },
              ]
            : []),
          {
            type: "band" as const,
            data: line.progress,
            y0: (d: VestingPoint) => d.exercised ?? 0,
            y1: (d: VestingPoint) => d.cumulative,
            color: seriesColor(line.index),
            opacity: 0.22,
            curve: "step" as const,
          },
        ])
      : []),
    ...lines.flatMap((line) =>
      line.cliffDate !== undefined
        ? [
            {
              type: "x-rule" as const,
              value: new Date(line.cliffDate).getTime(),
              dashed: true,
              opacity: 0.4,
              label: line.label ? `${t("options.cliff")} · ${line.label}` : t("options.cliff"),
              labelDy: line.index * 14,
            },
          ]
        : [],
    ),
    ...eventRuleComponents(
      lines.flatMap((line) => line.accelerationDates ?? []),
      xMin,
      xMax,
      t("options.acceleration"),
      0,
    ),
    ...eventRuleComponents(
      lines.flatMap((line) => line.cancellationDates ?? []),
      xMin,
      xMax,
      t("options.cancellation"),
      14,
    ),
    ...(windowBars.length > 0
      ? [
          {
            type: "x-ranges" as const,
            offsetY: EXERCISE_LANE.offsetY,
            thickness: EXERCISE_LANE.thickness,
            label: t("options.exercise-windows"),
            ranges: windowBars.map((bar) => ({
              from: new Date(bar.fromDate).getTime(),
              to: new Date(bar.toDate).getTime(),
              color: EXERCISE_WINDOW_COLORS[bar.status],
              title: tf({
                key: "options.exercise-window-period",
                params: {
                  from: { type: "date", value: bar.fromDate },
                  to: { type: "date", value: bar.toDate },
                },
              }),
            })),
          },
        ]
      : []),
    ...(showToday && todayMs !== undefined
      ? [
          {
            type: "x-rule" as const,
            value: todayMs,
            opacity: 0.7,
            extendAbove: laneHeight,
          },
        ]
      : []),
    ...lines.map((line) => ({
      type: "line" as const,
      data: line.points,
      color: seriesColor(line.index),
      curve: "step" as const,
      strokeWidth: showProgress ? 2.5 : 1.5,
    })),
    ...(showProgress
      ? []
      : lines.map((line) => ({
          type: "dots" as const,
          data: chartPoints.flatMap((point) =>
            point.vested
              .filter((vested) => vested.seriesIndex === line.index)
              .map((vested) => ({
                date: new Date(point.date).getTime(),
                cumulative: vested.cumulative,
              })),
          ),
          color: seriesColor(line.index),
          radius: 2.5,
        }))),
    ...lines.flatMap((line) =>
      line.cliffPoint
        ? [
            {
              type: "dots" as const,
              data: [line.cliffPoint],
              color: seriesColor(line.index),
              radius: 4,
              variant: "ring" as const,
            },
          ]
        : [],
    ),
    ...lines.map((line) => ({
      type: "dots" as const,
      data: line.markers.map((marker) => ({
        date: new Date(marker.date).getTime(),
        cumulative: marker.vested,
      })),
      color: "var(--color-secondary)",
      radius: 5,
    })),
    ...(summary === undefined
      ? []
      : [
          {
            type: "dots" as const,
            data: [{ date: new Date(summary.date).getTime(), cumulative: summary.vested }],
            color: seriesColor(0),
            radius: 5,
          },
        ]),
    { type: "hover-value" },
    { type: "annotations", annotations },
  ];
  const windowStatuses = (["open", "upcoming", "closed"] as const).filter((status) =>
    windowBars.some((bar) => bar.status === status),
  );
  const margin = {
    ...VESTING_CHART.margin,
    top: VESTING_CHART.margin.top + laneHeight,
    left: showProgress ? VESTING_CHART.margin.left + 16 : VESTING_CHART.margin.left,
    right: summary === undefined ? VESTING_CHART.margin.right : VESTING_CHART.summaryRight,
  };

  return (
    <VBox gap={"sm"}>
      {series.length > 1 && (
        <Box gap={"lg"} wrap justifyContent={"end"}>
          {lines.map((line) => (
            <Box key={line.label} alignItems={"center"} gap={"xs"}>
              <svg width={8} height={8} role={"presentation"}>
                <circle cx={4} cy={4} r={4} fill={seriesColor(line.index)} />
              </svg>
              <small className={"subtle"}>{line.label}</small>
            </Box>
          ))}
        </Box>
      )}
      <div className={"w-full"} style={{ height: VESTING_CHART.height + laneHeight }}>
        <AutoSizer>
          {({ width }) => (
            <Sparkline
              width={width}
              height={VESTING_CHART.height + laneHeight}
              margin={margin}
              data={hoverPoints}
              x={{ scaleType: "linear", val: (d) => d.date, domain: [xMin, xMax] }}
              y={{
                scaleType: "linear",
                val: (d) => d.cumulative,
                formatVal: (d) => d.tooltip ?? "",
                domain: [0, yMax],
              }}
              components={components}
            />
          )}
        </AutoSizer>
      </div>
      {showProgress && (
        <Box gap={"lg"} wrap justifyContent={"center"}>
          <ChartLegendItem
            swatch={"line"}
            color={"var(--color-primary)"}
            label={t("options.vesting-legend-vested")}
          />
          {hasExercises && (
            <ChartLegendItem
              swatch={"box"}
              color={"var(--color-bg-default)"}
              bordered
              label={t("options.vesting-legend-exercised")}
            />
          )}
          <ChartLegendItem
            swatch={"box"}
            color={"var(--color-primary)"}
            opacity={0.22}
            label={t("options.vesting-legend-exercisable")}
          />
          {windowStatuses.map((status) => (
            <ChartLegendItem
              key={status}
              swatch={"bar"}
              color={EXERCISE_WINDOW_COLORS[status]}
              label={t(EXERCISE_WINDOW_LEGEND[status])}
            />
          ))}
        </Box>
      )}
    </VBox>
  );
});

const VALUE_CHART = { height: 240, margin: { top: 24, left: 88, bottom: 28, right: 16 } };

const VALUE_CHART_TICK_MONTHS = [1, 3, 6, 12, 24, 60];

type OptionValueDatum = { date: number; point: OptionValuePoint };

export const OptionValueCard = observer(function OptionValueCard({
  points,
}: {
  points: OptionValuePoint[];
}) {
  const { t, f } = useTranslation();
  const data: OptionValueDatum[] = points.map((point) => ({
    date: new Date(point.date).getTime(),
    point,
  }));
  const xMin = data[0].date;
  const xMax = data[data.length - 1].date;
  const yMax = niceCeil(Math.max(1, ...points.map((p) => p.amount.value)));
  const spanMonths = (xMax - xMin) / (30.44 * 24 * 60 * 60 * 1000);
  const tickMonths = VALUE_CHART_TICK_MONTHS.find((months) => spanMonths / months <= 6) ?? 120;
  const xTicks: SparklineTick[] = [];
  for (let tick = new Date(xMin); tick.getTime() <= xMax; tick = plusMonths(tick, tickMonths)) {
    xTicks.push({ value: tick.getTime(), label: f.formatYearMonth(tick) });
  }
  const yTicks: SparklineTick[] = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ({
    value: fraction * yMax,
    label: f.formatNumber(fraction * yMax),
  }));

  return (
    <Card gap={"md"}>
      <VBox gap={0}>
        <strong>{t("options.value-heading")}</strong>
        <small className={"subtle"}>{t("options.value-description")}</small>
      </VBox>
      <div className={"w-full"} style={{ height: VALUE_CHART.height }}>
        <AutoSizer>
          {({ width }) => (
            <Sparkline
              width={width}
              height={VALUE_CHART.height}
              margin={VALUE_CHART.margin}
              data={data}
              x={{ scaleType: "linear", val: (d) => d.date, domain: [xMin, xMax] }}
              y={{
                scaleType: "linear",
                val: (d) => d.point.amount.value,
                formatVal: (d) => ({
                  type: "list",
                  value: [
                    { type: "date", value: d.point.date },
                    {
                      key: "options.value-tooltip-holder",
                      params: { amount: { type: "amount", ...d.point.amount } },
                    },
                    {
                      key: "options.value-tooltip-company",
                      params: { amount: { type: "amount", ...d.point.companyValue } },
                    },
                  ],
                  separator: "\n",
                }),
                domain: [0, yMax],
              }}
              components={[
                { type: "plot-background" },
                { type: "y-axis", ticks: yTicks },
                { type: "x-ticks", ticks: xTicks },
                { type: "line", color: seriesColor(0), curve: "linear", strokeWidth: 1.5 },
                { type: "dots", color: seriesColor(0) },
                { type: "hover-value" },
              ]}
            />
          )}
        </AutoSizer>
      </div>
    </Card>
  );
});

type ProgramSelectItem = { type: "plan"; plan: OptionPlan } | { type: "new-program" };

const newProgramSelectItem: ProgramSelectItem = { type: "new-program" };

const GrantStep = observer(function GrantStep({
  form,
  editedTrancheId,
}: {
  form: GrantForm;
  editedTrancheId?: string;
}) {
  const { t, tf } = useTranslation();
  const { optionsState } = useRootStore();
  const optionsAbovePool = optionsAbovePoolAfterGrant({
    plan: form.plan.value,
    numberOfOptions: form.numberOfOptions.toNumber(),
    grants: optionsPageData(optionsState)?.grants ?? [],
    today: toLocalDateISOString(today()),
    editedTrancheId,
  });
  const programItems: ProgramSelectItem[] = [
    ...form.plan.options
      .filter((plan) => plan !== undefined)
      .map((plan) => ({ type: "plan", plan }) as const),
    newProgramSelectItem,
  ];
  const programValue =
    form.plan.value === undefined ? undefined : ({ type: "plan", plan: form.plan.value } as const);

  return (
    <VBox gap={"xl"} marginY={"md"}>
      <Grid cols={1} colsMd={2} gap={"lg"} alignItems={"start"}>
        <Select
          label={t("options.program")}
          value={programValue}
          items={programItems}
          fullWidth
          onSelect={(item) =>
            item.type === "new-program" ? [openCreatePlanDialog()] : [selectGrantPlan(item.plan)]
          }
          idFn={(item) => (item.type === "new-program" ? "new-program" : item.plan.optionPlanId)}
          labelFn={(item) =>
            item.type === "new-program"
              ? { key: "options.new-program" }
              : { translated: item.plan.name }
          }
          iconFn={(item) => (item.type === "new-program" ? "plus" : undefined)}
          error={!form.plan.isValid}
          message={form.plan.error === undefined ? undefined : tf(form.plan.error)}
        />
        <MSelect label={t("options.options-for")} model={form.shareType} fullWidth />
        <MTextInput label={t("options.number-of-options")} model={form.numberOfOptions} />
        <MTextInput label={t("instrument.strike-price")} model={form.strikePrice} prefix={"NOK"} />
        <MDateInput label={t("instrument.grant-date")} model={form.grantDate} />
        <MDateInput label={t("instrument.expiration-date")} model={form.expirationDate} />
      </Grid>
      {optionsAbovePool > 0 && (
        <Alert
          type={"warning"}
          icon
          message={{
            key: "options.grant-above-pool",
            params: { count: { type: "number", value: optionsAbovePool } },
          }}
        />
      )}
      <MTextArea label={t("remarks")} model={form.remarks} rows={4} />
      {optionsState?.createPlan && <CreatePlanDialog create={optionsState.createPlan} />}
    </VBox>
  );
});

export const CreatePlanDialog = observer(function CreatePlanDialog({
  create,
}: {
  create: CreatePlanState;
}) {
  const { t } = useTranslation();
  const { orgNumber } = useUserStore();
  const dispatch = useDispatch();

  return (
    <Dialog
      title={{ key: "options.new-program" }}
      maxWidth={"sm"}
      isSubDialog
      onCancel={[closeCreatePlanDialog()]}
      onSubmit={() => dispatch(submitCreatePlan(orgNumber, create))}
      submitResult={create.submitResult}
      submitLabel={{ key: "create" }}
    >
      <VBox gap={"lg"}>
        <PlanFields plan={create} dialog={"create"} />
        <VBox gap={"sm"}>
          <strong>{t("attachments")}</strong>
          <AttachmentsPanel
            title={false}
            fileUploadConfig={optionAttachmentUploadConfig}
            uploadedAttachments={create.stagedFiles.map((staged) => ({
              id: staged.id,
              filename: staged.file.name,
            }))}
            onUpload={(files) => dispatch([stageCreatePlanFiles(files)])}
            onDelete={(id) => dispatch([removeStagedCreatePlanFile(id)])}
            onOpen={() => undefined}
            onInvalidFile={(message) => dispatch([showAlert(message, "error")])}
          />
        </VBox>
      </VBox>
    </Dialog>
  );
});

const PlanFields = observer(function PlanFields({
  plan,
  poolComment,
  dialog,
}: {
  plan: Pick<
    EditPlanState,
    | "name"
    | "description"
    | "expirationDate"
    | "poolSize"
    | "poolEffectiveDate"
    | "allowExerciseRequests"
    | "exerciseRequestTiming"
    | "exerciseWindows"
    | "exerciseWindowsError"
  >;
  poolComment?: StringFieldModel;
  dialog: PlanDialog;
}) {
  const { t } = useTranslation();
  const policy = planRequestPolicy(plan);
  return (
    <>
      <MTextInput label={t("options.program-name")} model={plan.name} />
      <MarkdownEditor
        label={t("options.description")}
        value={plan.description.inputValue}
        onChange={plan.description.setAndCommitInputValue}
      />
      <Grid cols={1} colsMd={3} gap={"lg"} alignItems={"start"}>
        <MTextInput label={<PoolSizeLabel />} model={plan.poolSize} />
        <MDateInput label={t("options.pool-effective-date")} model={plan.poolEffectiveDate} />
        <MDateInput label={<ProgramExpirationDateLabel />} model={plan.expirationDate} />
      </Grid>
      {poolComment && <MTextArea label={t("options.pool-comment")} model={poolComment} rows={2} />}
      <VBox gap={"md"}>
        <strong>{t("options.exercise-request-policy")}</strong>
        <MCheckbox
          model={plan.allowExerciseRequests}
          label={{ key: "options.allow-exercise-requests" }}
        />
        {policy !== "DISABLED" && <MRadioButtonGroup model={plan.exerciseRequestTiming} />}
      </VBox>
      {policy === "WINDOWS_ONLY" && (
        <ExerciseWindowsEditor rows={plan.exerciseWindows} dialog={dialog} />
      )}
      {policy === "WINDOWS_ONLY" && plan.exerciseWindowsError !== undefined && (
        <Alert type={"error"} icon message={plan.exerciseWindowsError} />
      )}
    </>
  );
});

const ExerciseWindowsEditor = observer(function ExerciseWindowsEditor({
  rows,
  dialog,
}: {
  rows: ExerciseWindowRow[];
  dialog: PlanDialog;
}) {
  const { t } = useTranslation();
  return (
    <VBox gap={"sm"}>
      <strong>
        {t("options.exercise-windows")}{" "}
        <InfoButton infoText={{ key: "options.exercise-windows-info" }} />
      </strong>
      {rows.length > 0 && (
        <Table variant={"IN_CARD"}>
          <thead>
            <tr>
              <th align={"left"}>{t("from")}</th>
              <th align={"left"}>{t("to")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <MDateInput model={row.fromDate} disabled={row.hasStarted} />
                </td>
                <td>
                  <MDateInput model={row.toDate} disabled={row.hasStarted} />
                </td>
                <td>
                  {!row.hasStarted && (
                    <IconButton
                      icon={"trash"}
                      color={"error"}
                      tooltip={t("delete")}
                      onClick={[removeExerciseWindow(dialog, row.id)]}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <Box>
        <LinkButton icon={"plus"} label={t("add")} onClick={[addExerciseWindow(dialog)]} />
      </Box>
    </VBox>
  );
});

function ProgramExpirationDateLabel() {
  const { t } = useTranslation();
  return (
    <>
      {t("instrument.expiration-date")}{" "}
      <InfoButton infoText={{ key: "options.program-expiration-date-info" }} />
    </>
  );
}

function PoolSizeLabel() {
  const { t } = useTranslation();
  return (
    <>
      {t("options.pool-size")} <InfoButton infoText={{ key: "options.pool-size-info" }} />
    </>
  );
}

export const PoolHistoryTable = observer(function PoolHistoryTable({
  poolChanges,
}: {
  poolChanges: OptionPoolChange[];
}) {
  const { t, f } = useTranslation();
  return (
    <Table withRowBorders>
      <thead>
        <tr>
          <th align={"left"}>{t("options.pool-effective-date")}</th>
          <th align={"right"}>{t("options.pool-size")}</th>
          <th align={"left"}>{t("remarks")}</th>
          <th align={"left"}>{t("options.pool-recorded")}</th>
        </tr>
      </thead>
      <tbody>
        {poolChanges.map((change) => (
          <tr key={change.eventId}>
            <td>{f.formatDate(change.effectiveDate)}</td>
            <td align={"right"}>{f.formatNumber(change.poolSize)}</td>
            <td>{change.comment ?? ""}</td>
            <td>{f.formatDate(change.recordedAt)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
});

export const EditPlanDialog = observer(function EditPlanDialog({
  editPlan,
  attachments,
}: {
  editPlan: EditPlanState;
  attachments: UploadedAttachment[];
}) {
  const { t } = useTranslation();
  const { orgNumber } = useUserStore();
  const dispatch = useDispatch();
  const poolChanges = editPlan.plan.poolChanges ?? [];

  return (
    <Dialog
      title={{ key: "options.edit-program" }}
      maxWidth={"sm"}
      onCancel={[closeEditPlanDialog()]}
      onSubmit={() => dispatch(submitEditPlan(orgNumber, editPlan))}
      submitResult={editPlan.submitResult}
      submitLabel={{ key: "save" }}
    >
      <VBox gap={"lg"}>
        <PlanFields plan={editPlan} poolComment={editPlan.poolComment} dialog={"edit"} />
        {poolChanges.length > 0 && (
          <VBox gap={"sm"}>
            <strong>{t("options.pool-history")}</strong>
            <PoolHistoryTable poolChanges={poolChanges} />
          </VBox>
        )}
        <VBox gap={"sm"}>
          <strong>{t("attachments")}</strong>
          <PlanAttachments
            planId={editPlan.plan.optionPlanId}
            attachments={attachments}
            orgNumber={orgNumber}
            editable
          />
        </VBox>
      </VBox>
    </Dialog>
  );
});

export const optionsTableColumns = (
  { tf, f, lang }: PreppedI18nState,
  shareTypes: ShareType[],
  { showProgram }: { showProgram: boolean },
): TableColumn<OptionsTableRow>[] => {
  const formatCount = (value: number) => f.formatNumber(value === 0 ? 0 : -value);
  const programLine = (row: OptionsTableRow) =>
    showProgram && (
      <small className={"subtle"}>
        {row.isMultipleProgram ? <i>{tf(row.program)}</i> : tf(row.program)}
      </small>
    );

  return [
    {
      name: "holder",
      label: { key: "instrument.holder" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.holderName === undefined ? (
          showProgram && (
            <Box alignItems={"center"} gap={"sm"}>
              <Icon name={"caret-right"} className={"invisible"} />
              {programLine(row)}
            </Box>
          )
        ) : (
          <Box alignItems={"center"} gap={"sm"}>
            <Icon
              name={
                row.expandable
                  ? row.expanded
                    ? "caret-down"
                    : "caret-right"
                  : (row.holderIcon ?? "user-square")
              }
            />
            <VBox gap={0}>
              <span>{row.holderName}</span>
              {programLine(row)}
            </VBox>
          </Box>
        ),
    },
    {
      name: "optionsFor",
      label: { key: "options.options-for" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.shareTypeId === undefined
          ? "–"
          : lookupShareTypeName(row.shareTypeId, shareTypes, lang),
    },
    {
      name: "strike",
      label: { key: "options.strike" },
      align: "right",
      notSorted: true,
      getValue: (row) => (row.strike === undefined ? "-" : f.formatAmount("NOK", row.strike)),
    },
    {
      name: "grantDate",
      label: { key: "instrument.grant-date" },
      align: "left",
      notSorted: true,
      getValue: (row) => (row.grantDate === undefined ? "-" : f.formatDate(row.grantDate)),
    },
    {
      name: "expiryDate",
      label: { key: "options.expiry-date" },
      align: "left",
      notSorted: true,
      getValue: (row) => (row.expiryDate === undefined ? "-" : f.formatDate(row.expiryDate)),
    },
    {
      name: "granted",
      label: { key: "options.granted" },
      align: "right",
      notSorted: true,
      getValue: (row) => f.formatNumber(row.granted),
    },
    {
      name: "exercised",
      label: { key: "options.exercised" },
      align: "right",
      notSorted: true,
      getValue: (row) => formatCount(row.exercised),
    },
    {
      name: "cancelled",
      label: { key: "options.cancelled" },
      align: "right",
      notSorted: true,
      getValue: (row) => formatCount(row.cancelled),
    },
    {
      name: "expired",
      label: { key: "options.expired" },
      align: "right",
      notSorted: true,
      getValue: (row) => formatCount(row.expired),
    },
    {
      name: "outstanding",
      label: { key: "options.outstanding" },
      align: "right",
      notSorted: true,
      getValue: (row) => f.formatNumber(row.outstanding),
    },
    {
      name: "vested",
      label: { key: "options.vested" },
      align: "right",
      notSorted: true,
      getValue: (row) => f.formatNumber(row.vested),
    },
  ];
};

export const ProgramsTab = observer(function ProgramsTab({
  plans,
  grants,
  today,
}: {
  plans: OptionPlan[];
  grants: OptionGrantDetails[];
  today: string;
}) {
  const { t, f } = useTranslation();
  const { optionsState } = useRootStore();
  const { orgNumber } = useUserStore();
  const rows = prepProgramPoolRows(plans, grants, today);

  const columns: TableColumn<ProgramPoolRow>[] = [
    {
      name: "program",
      label: { key: "options.program" },
      align: "left",
      notSorted: true,
      getValue: (row) => row.plan.name,
    },
    {
      name: "expirationDate",
      label: { key: "instrument.expiration-date" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.plan.expirationDate === undefined ? "–" : f.formatDate(row.plan.expirationDate),
    },
    {
      name: "exerciseWindows",
      label: { key: "options.exercise-windows" },
      align: "center",
      notSorted: true,
      getValue: (row) => {
        const exerciseWindows = activeExerciseWindows(row.plan);
        return exerciseWindows.length === 0 ? (
          "–"
        ) : (
          <TextWithTooltip
            text={{ translated: f.formatNumber(exerciseWindows.length) }}
            tooltip={exerciseWindowsTooltip(exerciseWindows)}
            underline={"solid"}
          />
        );
      },
    },
    {
      name: "poolSize",
      label: { key: "options.pool-size" },
      align: "right",
      notSorted: true,
      getValue: (row) => (row.poolSize === undefined ? "–" : f.formatNumber(row.poolSize)),
    },
    {
      name: "used",
      label: { key: "options.pool-used" },
      tooltipLabel: t("options.pool-used-help"),
      align: "right",
      notSorted: true,
      getValue: (row) => f.formatNumber(row.taken),
    },
    {
      name: "unallocated",
      label: { key: "options.pool-unallocated" },
      align: "right",
      notSorted: true,
      getValue: (row) =>
        row.unallocated === undefined ? (
          "–"
        ) : row.unallocated < 0 ? (
          <Tag
            label={{
              key: "options.pool-above",
              params: { count: { type: "number", value: -row.unallocated } },
            }}
            color={"warning"}
            icon={"warning"}
            compact
          />
        ) : (
          f.formatNumber(row.unallocated)
        ),
    },
  ];

  return (
    <VBox gap={"md"}>
      <DataTable
        hoverable
        tableColumns={columns}
        tableData={rows}
        getId={(row) => row.plan.optionPlanId}
        onRowClick={(row) => [navigate(adminOptionProgram(orgNumber, row.plan.optionPlanId))]}
        rowActions={(row) => [
          [
            "context-menu",
            [
              {
                type: "item",
                icon: "edit",
                label: { key: "options.edit" },
                onSelect: [openEditPlanDialog(row.plan, today)],
              },
            ],
          ],
        ]}
      />
      {optionsState?.editPlan && (
        <EditPlanDialog
          editPlan={optionsState.editPlan}
          attachments={planAttachments(plans, optionsState.editPlan.plan.optionPlanId)}
        />
      )}
    </VBox>
  );
});

const EXERCISE_TABS: ExerciseTab[] = ["ALL", "REQUESTED", "ACKNOWLEDGED", "COMPLETED"];

type ExercisePlanFilterItem = { type: "all" } | { type: "plan"; plan: OptionPlan };

export const ExerciseStatusTabs = observer(function ExerciseStatusTabs({
  grants,
  plans,
  holders,
}: {
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
}) {
  const { optionsState } = useRootStore();
  const filter = optionsState?.exercisesFilter;
  if (filter === undefined) {
    return null;
  }
  const requestedCount = exerciseStageCount(exerciseListRows(grants, plans, holders), "REQUESTED");
  return (
    <Tabs
      scrollable={false}
      tabs={EXERCISE_TABS.map((tab) => ({
        id: tab,
        label: { key: tab === "ALL" ? "showAll" : exerciseTabLabelKeys[tab] },
        isActive: filter.tab === tab,
        onSelect: [setExercisesTab(tab)],
        tag:
          tab === "REQUESTED" && requestedCount > 0
            ? { label: String(requestedCount), color: "error", compact: true }
            : undefined,
      }))}
    />
  );
});

export const ExercisesTab = observer(function ExercisesTab({
  grants,
  plans,
  holders,
  shareTypes,
}: {
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
}) {
  const { t, lang } = useTranslation();
  const { orgNumber } = useUserStore();
  const { optionsState } = useRootStore();
  const filter = optionsState?.exercisesFilter;
  if (filter === undefined) {
    return null;
  }
  const todayIso = toLocalDateISOString(today());
  const rows = exerciseListRows(grants, plans, holders);
  const visibleRows = filterExerciseRows(rows, filter);
  const selectedPlan = plans.find((plan) => plan.optionPlanId === filter.planId);
  const planItems: ExercisePlanFilterItem[] = [
    { type: "all" },
    ...plans.map((plan) => ({ type: "plan", plan }) as const),
  ];

  const actionScope = { orgNo: orgNumber, grants, plans, holders, today: todayIso };
  const acknowledgedDate = (row: ExerciseListRow): string | undefined => {
    const decision = row.request?.decision;
    return decision?.status === "ACKNOWLEDGED"
      ? toLocalDateISOString(new Date(decision.changedAt))
      : undefined;
  };

  const columns: TableColumn<ExerciseListRow>[] = [
    {
      name: "holder",
      label: { key: "instrument.holder" },
      align: "left",
      notSorted: true,
      getValue: (row) => (
        <Box alignItems={"center"} gap={"sm"}>
          <Icon name={"file-text"} />
          <VBox gap={0}>
            <span>{row.holderName}</span>
            <small className={"subtle"}>{t(row.program)}</small>
          </VBox>
        </Box>
      ),
    },
    {
      name: "status",
      label: { key: "status" },
      align: "left",
      notSorted: true,
      getValue: (row) => <Tag {...exerciseStageTag(row.stage)} />,
    },
    {
      name: "requested",
      label: { key: "options.exercise-status-requested" },
      align: "right",
      notSorted: true,
      getValue: (row) =>
        row.request === undefined ? null : { type: "number", value: row.request.request.count },
    },
    {
      name: "shareClass",
      label: { key: "shareType" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.shareTypeId === undefined
          ? null
          : lookupShareTypeName(row.shareTypeId, shareTypes, lang),
    },
    {
      name: "requestDate",
      label: { key: "options.request-date" },
      align: "left",
      notSorted: true,
      getValue: (row) =>
        row.request === undefined ? null : { type: "date", value: row.request.request.requestDate },
    },
    {
      name: "acknowledged",
      label: { key: "options.exercise-status-acknowledged" },
      align: "left",
      notSorted: true,
      getValue: (row) => {
        const date = acknowledgedDate(row);
        return date === undefined ? null : { type: "date", value: date };
      },
    },
    {
      name: "exercised",
      label: { key: "options.exercise-shares-exercised" },
      align: "right",
      notSorted: true,
      getValue: (row) =>
        row.kind === "exercise" ? { type: "number", value: row.exercise.count } : null,
    },
  ];

  return (
    <VBox gap={"md"}>
      {rows.length === 0 ? (
        <EmptyState illustration={"check-circle"} title={{ key: "options.no-exercises" }} subtle />
      ) : (
        <>
          <Box gap={"sm"} wrap alignItems={"center"}>
            <MTextInput
              model={filter.search}
              name={"exercises_search"}
              placeholder={t("search")}
              icon={"magnifying-glass"}
              type={"search"}
              inline
            />
            <Select
              placeholder={t("options.program")}
              grow={false}
              value={selectedPlan === undefined ? undefined : { type: "plan", plan: selectedPlan }}
              items={planItems}
              idFn={(item) => (item.type === "all" ? "all" : item.plan.optionPlanId)}
              labelFn={(item) =>
                item.type === "all"
                  ? { key: "options.all-programs" }
                  : { translated: item.plan.name }
              }
              onSelect={(item) => [
                setExercisesPlan(item.type === "all" ? undefined : item.plan.optionPlanId),
              ]}
            />
          </Box>
          {visibleRows.length === 0 ? (
            <span className={"subtle"}>{t("options.no-matching-exercises")}</span>
          ) : (
            <DataTable
              tableColumns={columns}
              tableData={visibleRows}
              getId={(row) => `${row.kind}-${row.id}`}
              rowActions={(row) => exerciseRowActions(row, actionScope)}
            />
          )}
        </>
      )}
      {optionsState?.reverseExercise && (
        <ReverseExerciseDialog reverseExercise={optionsState.reverseExercise} />
      )}
    </VBox>
  );
});

const ReverseExerciseDialog = observer(function ReverseExerciseDialog({
  reverseExercise,
}: {
  reverseExercise: ReverseExerciseState;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: "options.reverse-exercise-for", params: { name: reverseExercise.holderName } }}
      onCancel={[closeReverseExerciseDialog()]}
      onSubmit={() => dispatch(submitReverseExercise(orgNumber, reverseExercise))}
      submitResult={reverseExercise.submitResult}
      submitLabel={{ key: "options.reverse-exercise" }}
    >
      <VBox gap={"md"}>
        <p>
          {t({
            key: "options.reverse-exercise-description",
            params: {
              program: reverseExercise.program,
              options: { type: "number", value: reverseExercise.count },
            },
          })}
        </p>
        <MTextArea label={t("remarks")} model={reverseExercise.remarks} rows={3} />
      </VBox>
    </Dialog>
  );
});

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <Box justifyContent={"space-between"} gap={"lg"}>
      <span className={"subtle"}>{label}</span>
      <span>{value}</span>
    </Box>
  );
}

function RemarkText({ remarks }: { remarks?: string }) {
  const { t } = useTranslation();
  return remarks !== undefined && remarks !== "" ? (
    <span className={"whitespace-pre-wrap"}>{remarks}</span>
  ) : (
    <span className={"subtle"}>{t("options.no-remarks")}</span>
  );
}

const TrancheAttachments = observer(function TrancheAttachments({
  trancheId,
  attachments,
  orgNumber,
  editable,
}: {
  trancheId: string;
  attachments: UploadedAttachment[];
  orgNumber: string;
  editable: boolean;
}) {
  const dispatch = useDispatch();
  return (
    <AttachmentsPanel
      title={false}
      fileUploadConfig={editable ? optionAttachmentUploadConfig : undefined}
      uploadedAttachments={attachments}
      onUpload={
        editable
          ? (files) => dispatch([uploadTrancheAttachments(orgNumber, trancheId, files)])
          : undefined
      }
      onOpen={(id) =>
        dispatch([
          openAttachmentPreview(
            attachments.find((attachment) => attachment.id === id)?.filename ?? "",
            trancheAttachmentUrl(orgNumber, trancheId, id),
          ),
        ])
      }
      onDelete={
        editable ? (id) => dispatch([deleteTrancheAttachment(orgNumber, trancheId, id)]) : undefined
      }
      onInvalidFile={editable ? (message) => dispatch([showAlert(message, "error")]) : undefined}
    />
  );
});

const PlanAttachments = observer(function PlanAttachments({
  planId,
  attachments,
  orgNumber,
  editable,
}: {
  planId: string;
  attachments: UploadedAttachment[];
  orgNumber: string;
  editable: boolean;
}) {
  const dispatch = useDispatch();
  return (
    <AttachmentsPanel
      title={false}
      fileUploadConfig={editable ? optionAttachmentUploadConfig : undefined}
      uploadedAttachments={attachments}
      onUpload={
        editable
          ? (files) => dispatch([uploadPlanAttachments(orgNumber, planId, files)])
          : undefined
      }
      onOpen={(id) =>
        dispatch([
          openAttachmentPreview(
            attachments.find((attachment) => attachment.id === id)?.filename ?? "",
            planAttachmentUrl(orgNumber, planId, id),
          ),
        ])
      }
      onDelete={
        editable ? (id) => dispatch([deletePlanAttachment(orgNumber, planId, id)]) : undefined
      }
      onInvalidFile={editable ? (message) => dispatch([showAlert(message, "error")]) : undefined}
    />
  );
});

export const AttachmentPreviewDialog = observer(function AttachmentPreviewDialog() {
  const { optionsState } = useRootStore();
  const dispatch = useDispatch();
  return optionsState?.attachmentPreview ? (
    <FilePreviewDialog
      filename={optionsState.attachmentPreview.filename}
      file={{ url: optionsState.attachmentPreview.url }}
      onClose={() => dispatch([closeAttachmentPreview()])}
    />
  ) : null;
});

const GrantSection = observer(function GrantSection({
  tranche: trancheWithPlan,
  holderName,
  grants,
  plans,
  holders,
  shareTypes,
  fullyDiluted,
  today,
  grantNumber,
  grantCount,
  valueCard,
  addGrantButton,
  requestButton,
  readOnly,
}: {
  tranche: TrancheWithPlan;
  holderName: string;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  fullyDiluted?: FullyDilutedBasis;
  today: string;
  // 1-based position within its program; undefined when the program has a single grant
  grantNumber?: number;
  grantCount: number;
  valueCard?: ReactNode;
  addGrantButton?: ReactNode;
  requestButton?: ReactNode;
  readOnly?: boolean;
}) {
  const { t, tf, f, lang } = useTranslation();
  const { orgNumber } = useUserStore();
  const { tranche, planName } = trancheWithPlan;
  const numbers = trancheNumbers(tranche, today);
  const cards = prepHolderOptionCards({
    tranches: [trancheWithPlan],
    shareTypes,
    fullyDiluted,
    lang,
    today,
    scope: "tranche",
  });
  const series = prepHolderVestingSeries([trancheWithPlan]);
  const milestoneGroups = prepHolderMilestones([trancheWithPlan]);
  const formatCount = (value: number) => f.formatNumber(value === 0 ? 0 : -value);
  const shareTypeName =
    tranche.shareTypeId === undefined
      ? "–"
      : lookupShareTypeName(tranche.shareTypeId, shareTypes, lang);

  return (
    <VBox gap={"lg"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <Box alignItems={"center"} gap={"sm"}>
          <Icon name={"seal-check"} size={"lg"} />
          <VBox gap={0}>
            <h3>{planName}</h3>
            {grantNumber !== undefined && (
              <small className={"subtle"}>
                {t({
                  key: "options.grant-x-of-y",
                  params: { number: grantNumber, total: grantCount },
                })}
              </small>
            )}
          </VBox>
        </Box>
        {readOnly && requestButton}
        {!readOnly && (
          <Box alignItems={"center"} gap={"sm"}>
            {addGrantButton}
            <DropdownMenu
              button={{ type: "button", variant: "secondary", label: t("edit") }}
              alignment={"end"}
              items={grantActionItems({
                orgNo: orgNumber,
                tranche,
                holderName,
                program: programLabel(planName, tranche.name),
                grants,
                plans,
                holders,
                shareTypes,
                lang,
                today,
                t,
              })}
            />
          </Box>
        )}
      </Box>
      <Grid cols={1} colsMd={cards.length > 3 ? 2 : 3} colsLg={cards.length} gap={"lg"}>
        {cards.map((card, index) => (
          <DataCard key={index} {...card} />
        ))}
      </Grid>
      {valueCard}
      <Grid cols={1} colsMd={2} gap={"lg"}>
        <Card gap={"md"}>
          <strong>{t("options.grant")}</strong>
          <VBox gap={"sm"}>
            <DetailRow label={t("options.options-for")} value={shareTypeName} />
            <DetailRow
              label={t("options.vesting-type")}
              value={tf(vestingTypeLabel(tranche.vesting))}
            />
            <DetailRow
              label={t("instrument.strike-price")}
              value={f.formatAmount("NOK", tranche.strikePrice)}
            />
            <DetailRow label={t("instrument.grant-date")} value={f.formatDate(tranche.grantDate)} />
            <DetailRow
              label={t("options.expiry-date")}
              value={f.formatDate(tranche.expirationDate)}
            />
          </VBox>
        </Card>
        <Card gap={"md"}>
          <strong>{t("common.status")}</strong>
          <VBox gap={"sm"}>
            <DetailRow label={t("options.granted")} value={f.formatNumber(numbers.granted)} />
            <DetailRow label={t("options.vested")} value={f.formatNumber(numbers.vested)} />
            <DetailRow label={t("options.exercised")} value={formatCount(numbers.exercised)} />
            {awaitingShares(tranche, today) > 0 && (
              <DetailRow
                label={t("options.awaiting-shares")}
                value={f.formatNumber(awaitingShares(tranche, today))}
              />
            )}
            <DetailRow label={t("options.cancelled")} value={formatCount(numbers.cancelled)} />
            <DetailRow label={t("options.expired")} value={formatCount(numbers.expired)} />
            <DetailRow
              label={t("options.outstanding")}
              value={f.formatNumber(numbers.outstanding)}
            />
          </VBox>
        </Card>
      </Grid>
      {(series.length > 0 || milestoneGroups.length > 0) && (
        <Card gap={"md"}>
          <Box justifyContent={"space-between"} alignItems={"center"}>
            <strong>{t("options.vesting-and-exercise-heading")}</strong>
            {!readOnly && (
              <LinkButton
                icon={"edit"}
                label={t("edit")}
                onClick={[startEditVesting(tranche, holderName, grants, plans, shareTypes, lang)]}
              />
            )}
          </Box>
          {series.length > 0 && (
            <VestingChart
              series={series}
              today={today}
              exerciseWindows={trancheWithPlan.exerciseWindows}
            />
          )}
          {milestoneGroups.map((group) => (
            <HolderMilestones key={group.id} group={group} />
          ))}
        </Card>
      )}
      {!readOnly && (
        <>
          <Card gap={"md"}>
            <Box justifyContent={"space-between"} alignItems={"center"}>
              <strong>{t("remarks")}</strong>
              <LinkButton
                icon={"edit"}
                label={t("edit")}
                onClick={[startEditGrant(tranche, holderName, grants, plans, shareTypes, lang)]}
              />
            </Box>
            <RemarkText remarks={tranche.remarks} />
          </Card>
          <Card gap={"md"}>
            <Box justifyContent={"space-between"} alignItems={"center"}>
              <strong>{t("attachments")}</strong>
              <LinkButton
                icon={"edit"}
                label={t("edit")}
                onClick={[startEditGrant(tranche, holderName, grants, plans, shareTypes, lang)]}
              />
            </Box>
            {(tranche.attachments?.length ?? 0) > 0 ? (
              <TrancheAttachments
                trancheId={tranche.optionTrancheId}
                attachments={tranche.attachments ?? []}
                orgNumber={orgNumber}
                editable={false}
              />
            ) : (
              <span className={"subtle"}>{t("options.no-attachments")}</span>
            )}
          </Card>
        </>
      )}
    </VBox>
  );
});

const HolderOptionsContent = observer(function HolderOptionsContent({
  holder,
  grants,
  plans,
  holders,
  shareTypes,
  fairValues,
  fullyDiluted,
  companyName,
  readOnly,
  holderRequests,
}: {
  holder: InstrumentHolder;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  holders: InstrumentHolder[];
  shareTypes: ShareType[];
  fairValues: ShareValuationInformation[];
  fullyDiluted?: FullyDilutedBasis;
  companyName: string;
  readOnly?: boolean;
  holderRequests?: HolderExerciseRequestScope;
}) {
  const { t, tf, f, lang } = useTranslation();
  const { orgNumber } = useUserStore();
  const todayIso = toLocalDateISOString(today());
  const tranches = holderTranches(holder.instrumentHolderId, grants, plans);
  const cards = prepHolderOptionCards({
    tranches,
    shareTypes,
    fullyDiluted,
    lang,
    today: todayIso,
    scope: "holder",
  });
  const hasMultipleGrants = tranches.length > 1;
  const programGroups = groupTranchesByProgram(tranches);
  const history = prepHolderHistory(holder.instrumentHolderId, grants, plans, shareTypes);
  const values = prepHolderOptionValues({ tranches, fairValues, shareTypes, today: todayIso });
  const valueCard = values.length > 1 ? <OptionValueCard points={values} /> : undefined;
  const addGrantButton = readOnly ? undefined : (
    <Button
      variant={"secondary"}
      icon={"plus"}
      label={{ key: "options.add-grant" }}
      onClick={[startGrantForHolder(holder, lang, plans, shareTypes)]}
    />
  );
  const requests = readOnly
    ? []
    : holderExerciseRequests(holder.instrumentHolderId, grants, plans, holders, todayIso);
  const actionScope = { orgNo: orgNumber, grants, plans, holders, today: todayIso };

  return (
    <>
      <AdminExerciseRequestsCard views={requests} shareTypes={shareTypes} scope={actionScope} />
      {hasMultipleGrants && (
        <>
          <Box justifyContent={"space-between"} alignItems={"center"}>
            <VBox gap={0}>
              <h3>{t({ key: "options.options-in", params: { company: companyName } })}</h3>
              <small className={"subtle"}>
                {t({ key: "options.active-grants", params: { count: tranches.length } })}
              </small>
            </VBox>
            {addGrantButton}
          </Box>
          <Grid cols={1} colsMd={cards.length > 3 ? 2 : 3} colsLg={cards.length} gap={"lg"}>
            {cards.map((card, index) => (
              <DataCard key={index} {...card} />
            ))}
          </Grid>
          {valueCard}
        </>
      )}
      <VBox gap={"xl"}>
        {programGroups.flatMap((group) =>
          group.tranches.map((tranche, index) => (
            <GrantSection
              key={tranche.tranche.optionTrancheId}
              tranche={tranche}
              holderName={holder.name}
              grants={grants}
              plans={plans}
              holders={holders}
              shareTypes={shareTypes}
              fullyDiluted={fullyDiluted}
              today={todayIso}
              grantNumber={group.tranches.length > 1 ? index + 1 : undefined}
              grantCount={group.tranches.length}
              valueCard={hasMultipleGrants ? undefined : valueCard}
              addGrantButton={hasMultipleGrants ? undefined : addGrantButton}
              requestButton={
                holderRequests !== undefined && (
                  <HolderRequestExerciseButton
                    tranche={tranche.tranche}
                    grants={grants}
                    plans={plans}
                    today={todayIso}
                  />
                )
              }
              readOnly={readOnly}
            />
          )),
        )}
      </VBox>
      {history.length > 0 && (
        <Card gap={"md"}>
          <strong>{t("options.history")}</strong>
          <Table withRowBorders>
            <thead>
              <tr>
                <th align={"left"}>{t("options.event")}</th>
                <th align={"left"}>{t("options.description")}</th>
                <th align={"left"}>{t("date")}</th>
              </tr>
            </thead>
            <tbody>
              {history.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    <Box alignItems={"center"} gap={"sm"}>
                      <Icon name={entry.icon} />
                      <span>{tf(entry.event)}</span>
                    </Box>
                  </td>
                  <td>{tf(entry.description)}</td>
                  <td>{f.formatDate(entry.date)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
});

const HolderRequestExerciseButton = observer(function HolderRequestExerciseButton({
  tranche,
  grants,
  plans,
  today,
}: {
  tranche: OptionTranche;
  grants: OptionGrantDetails[];
  plans: OptionPlan[];
  today: string;
}) {
  const { t } = useTranslation();
  const button = holderRequestButton(tranche, grants, plans, today);
  if (button === undefined) {
    return null;
  }
  const requestButton = (
    <Button
      variant={"secondary"}
      icon={"plus"}
      label={{ key: "options.request-exercise" }}
      disabled={button.isDisabled}
      tooltip={
        button.tooltip.length > 0 ? button.tooltip.map((line) => t(line)).join("  \n") : undefined
      }
      onClick={[startExerciseRequest(today, tranche.optionTrancheId)]}
    />
  );
  return button.notice === undefined ? (
    requestButton
  ) : (
    <Box alignItems={"center"} gap={"md"}>
      <div className={"hidden md:block"}>
        <Alert icon type={"info"} message={button.notice} />
      </div>
      {requestButton}
    </Box>
  );
});

export const RecipientOptionsTab = observer(function RecipientOptionsTab({
  options,
  shareTypes,
  fairValues,
  companyName,
  holderRequests,
}: {
  options: ShareholderOptionsView;
  shareTypes: ShareType[];
  fairValues: ShareValuationInformation[];
  companyName: string;
  holderRequests?: HolderExerciseRequestScope;
}) {
  const { optionsState } = useRootStore();
  const requests = holderExerciseRequests(
    options.holder.instrumentHolderId,
    options.grants,
    options.plans,
    [options.holder],
    toLocalDateISOString(today()),
  );
  return (
    <TabContent
      content={
        <>
          <HolderExerciseRequestsCard
            views={requests}
            shareTypes={shareTypes}
            companyName={companyName}
            canManage={holderRequests !== undefined}
          />
          <HolderOptionsContent
            holder={options.holder}
            grants={options.grants}
            plans={options.plans}
            holders={[options.holder]}
            shareTypes={shareTypes}
            fairValues={fairValues}
            companyName={companyName}
            readOnly
            holderRequests={holderRequests}
          />
          {holderRequests !== undefined && optionsState?.exerciseRequestForm && (
            <ExerciseRequestFormDialog
              form={optionsState.exerciseRequestForm}
              candidates={exerciseRequestCandidates(options.grants, options.plans, [
                options.holder,
              ])}
              shareTypes={shareTypes}
              today={toLocalDateISOString(today())}
              holderScope={holderRequests}
            />
          )}
          {holderRequests !== undefined && optionsState?.deleteExerciseRequest && (
            <DeleteExerciseRequestDialog
              state={optionsState.deleteExerciseRequest}
              holderScope={holderRequests}
            />
          )}
        </>
      }
    />
  );
});

export const ShareholderOptionsTab = observer(function ShareholderOptionsTab({
  shareholderId,
  fairValues,
}: {
  shareholderId: number;
  fairValues: ShareValuationInformation[];
}) {
  const { optionsState } = useRootStore();
  const { companyName } = useUserStore();
  const { activeShareTypes, isLoadingShareTypes } = useShareholdersStore();
  const data = optionsPageData(optionsState);

  if (data === undefined || isLoadingShareTypes) {
    return <TabContent loading />;
  }

  const holder = holderForShareholder(data.holders, shareholderId);

  if (holder === undefined) {
    return (
      <TabContent emptyState={{ illustration: "file-x", title: { key: "options.empty-state" } }} />
    );
  }

  const todayIso = toLocalDateISOString(today());

  return (
    <TabContent
      content={
        <>
          <HolderOptionsContent
            holder={holder}
            grants={data.grants}
            plans={data.plans}
            holders={data.holders}
            shareTypes={activeShareTypes}
            fairValues={fairValues}
            fullyDiluted={fullyDilutedBasisFor(optionsState, shareholderId)}
            companyName={companyName ?? ""}
          />
          <OptionEditDialogs grants={data.grants} />
          {optionsState?.cancellation && (
            <RegisterCancellationDialog
              cancellation={optionsState.cancellation}
              tranches={allTranchesWithPlan(data.grants, data.plans)}
              today={todayIso}
            />
          )}
          {optionsState?.acceleration && (
            <RegisterAccelerationDialog
              acceleration={optionsState.acceleration}
              tranches={allTranchesWithPlan(data.grants, data.plans)}
              today={todayIso}
            />
          )}
          {optionsState?.exercise && (
            <RegisterExerciseDialog
              exercise={optionsState.exercise}
              grants={data.grants}
              plans={data.plans}
              holders={data.holders}
              shareTypes={activeShareTypes}
              today={todayIso}
            />
          )}
          <AttachmentPreviewDialog />
          <ExerciseRequestDialogs
            grants={data.grants}
            plans={data.plans}
            holders={data.holders}
            shareTypes={activeShareTypes}
            today={todayIso}
          />
          {optionsState?.registerGrant && (
            <RegisterGrantDialog
              registerGrant={optionsState.registerGrant}
              holders={data.holders}
              plans={data.plans}
            />
          )}
        </>
      }
    />
  );
});

export const OptionEditDialogs = observer(function OptionEditDialogs({
  grants,
}: {
  grants: OptionGrantDetails[];
}) {
  const { optionsState } = useRootStore();
  const editGrant = optionsState?.editGrant;
  const editVesting = optionsState?.editVesting;
  const deleteGrant = optionsState?.deleteGrant;

  return (
    <>
      {editGrant && (
        <EditGrantDialog
          edit={editGrant}
          attachments={findTranche(grants, editGrant.trancheId)?.attachments ?? []}
        />
      )}
      {editVesting && <EditVestingDialog edit={editVesting} />}
      {deleteGrant && <DeleteGrantDialog deleteGrant={deleteGrant} />}
      {optionsState?.isCreatingGrantLetter && (
        <Dialog>
          <LoadingIndicator label={{ key: "options.preparing-grant-letter" }} />
        </Dialog>
      )}
    </>
  );
});

const EditGrantDialog = observer(function EditGrantDialog({
  edit,
  attachments,
}: {
  edit: EditGrantState;
  attachments: UploadedAttachment[];
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <Dialog
      title={{ key: "options.edit-grant-for", params: { name: edit.holderName } }}
      onCancel={[closeEditGrantDialog()]}
      onSubmit={() => dispatch(submitEditGrant(orgNumber, edit))}
      submitResult={edit.submitResult}
      submitLabel={{ key: "save" }}
    >
      <GrantStep form={edit.grantForm} editedTrancheId={edit.trancheId} />
      <VBox gap={"sm"}>
        <strong>{t("attachments")}</strong>
        <TrancheAttachments
          trancheId={edit.trancheId}
          attachments={attachments}
          orgNumber={orgNumber}
          editable
        />
      </VBox>
    </Dialog>
  );
});

const EditVestingDialog = observer(function EditVestingDialog({
  edit,
}: {
  edit: EditVestingState;
}) {
  const { orgNumber } = useUserStore();
  const dispatch = useDispatch();

  return (
    <Dialog
      title={{ key: "options.edit-vesting-for", params: { name: edit.holderName } }}
      onCancel={[closeEditVestingDialog()]}
      onSubmit={() => dispatch(submitEditVesting(orgNumber, edit))}
      submitResult={edit.submitResult}
      submitLabel={{ key: "save" }}
      isSubmitDisabled={
        isPerformanceVesting(edit.vestingForm) &&
        !performanceMilestonesComplete(edit.grantForm, edit.vestingForm)
      }
    >
      <VestingStep
        grantForm={edit.grantForm}
        vestingForm={edit.vestingForm}
        showAll={edit.showAllVestingEntries ?? false}
      />
    </Dialog>
  );
});

export const ProgramDescriptionCard = observer(function ProgramDescriptionCard({
  plan,
  today,
}: {
  plan: OptionPlan;
  today: string;
}) {
  const { t } = useTranslation();
  return (
    <Card gap={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <strong>{t("options.description")}</strong>
        <LinkButton icon={"edit"} label={t("edit")} onClick={[openEditPlanDialog(plan, today)]} />
      </Box>
      {plan.description === undefined || plan.description === "" ? (
        <span className={"subtle"}>{t("options.no-program-description")}</span>
      ) : (
        <Markdown value={plan.description} disableLastMargin enableLinks />
      )}
    </Card>
  );
});

export const ProgramAttachmentsCard = observer(function ProgramAttachmentsCard({
  plan,
  today,
}: {
  plan: OptionPlan;
  today: string;
}) {
  const { t } = useTranslation();
  const { orgNumber } = useUserStore();
  const attachments = plan.attachments ?? [];
  return (
    <Card gap={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <strong>{t("attachments")}</strong>
        <LinkButton icon={"edit"} label={t("edit")} onClick={[openEditPlanDialog(plan, today)]} />
      </Box>
      {attachments.length > 0 ? (
        <PlanAttachments
          planId={plan.optionPlanId}
          attachments={attachments}
          orgNumber={orgNumber}
          editable={false}
        />
      ) : (
        <span className={"subtle"}>{t("options.no-attachments")}</span>
      )}
    </Card>
  );
});

const exerciseWindowStatusTags: Record<
  ExerciseWindowStatus,
  { label: TMessageKey; color: TagColor }
> = {
  closed: { label: "options.exercise-window-closed", color: "neutral" },
  open: { label: "options.exercise-window-open", color: "success" },
  upcoming: { label: "options.exercise-window-upcoming", color: "info" },
};

export const ExerciseRequestPolicyCard = observer(function ExerciseRequestPolicyCard({
  plan,
  today,
}: {
  plan: OptionPlan;
  today: string;
}) {
  const { t, f } = useTranslation();
  const policy = plan.exerciseRequestPolicy ?? "DISABLED";
  const windows = activeExerciseWindows(plan);
  return (
    <Card gap={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <strong>{t("options.exercise-request-policy")}</strong>
        <LinkButton icon={"edit"} label={t("edit")} onClick={[openEditPlanDialog(plan, today)]} />
      </Box>
      {policy === "DISABLED" ? (
        <span className={"subtle"}>{t("options.exercise-requests-disabled")}</span>
      ) : (
        <VBox gap={0}>
          <span>{t(exerciseRequestTimingLabel(policy))}</span>
          <small className={"subtle"}>{t(exerciseRequestTimingDescription(policy))}</small>
        </VBox>
      )}
      {policy !== "WINDOWS_ONLY" ? null : windows.length > 0 ? (
        <Table variant={"IN_CARD"}>
          <thead>
            <tr>
              <th align={"left"}>{t("from")}</th>
              <th align={"left"}>{t("to")}</th>
              <th align={"left"} />
            </tr>
          </thead>
          <tbody>
            {windows.map((exerciseWindow) => {
              const tag = exerciseWindowStatusTags[exerciseWindowStatus(exerciseWindow, today)];
              return (
                <tr key={exerciseWindow.fromDate}>
                  <td>{f.formatDate(exerciseWindow.fromDate)}</td>
                  <td>{f.formatDate(exerciseWindow.toDate)}</td>
                  <td>
                    <Tag label={{ key: tag.label }} color={tag.color} compact />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <span className={"subtle"}>{t("options.no-exercise-windows")}</span>
      )}
    </Card>
  );
});

export const VestingTemplateCard = observer(function VestingTemplateCard({
  plan,
  today,
}: {
  plan: OptionPlan;
  today: string;
}) {
  const { t, tf, f, lang } = useTranslation();
  const summary = prepVestingTemplateSummary(plan);

  return (
    <Card gap={"md"}>
      <Box justifyContent={"space-between"} alignItems={"center"}>
        <strong>{t("options.vesting-template")}</strong>
        <LinkButton
          icon={"edit"}
          label={t("edit")}
          onClick={[startEditPlanVesting(plan, today, lang)]}
        />
      </Box>
      {summary === undefined ? (
        <span className={"subtle"}>{t("options.no-vesting-template")}</span>
      ) : (
        <>
          <VBox gap={"sm"}>
            {summary.details.map((detail, index) => (
              <DetailRow key={index} label={tf(detail.label)} value={tf(detail.value)} />
            ))}
          </VBox>
          {summary.dates.length > 0 && (
            <Table withRowBorders>
              <thead>
                <tr>
                  <th align={"left"}>{t("instrument.vesting-date")}</th>
                  <th align={"left"}>{t("options.options-header")}</th>
                  <th align={"left"}>{t("options.percent-of-total")}</th>
                </tr>
              </thead>
              <tbody>
                {summary.dates.map((entry) => (
                  <tr key={entry.date}>
                    <td>{f.formatDate(entry.date)}</td>
                    <td>{f.formatNumber(entry.options)}</td>
                    <td>
                      {f.formatPercent(entry.fractionOfTotal, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {summary.milestones !== undefined && <HolderMilestones group={summary.milestones} />}
        </>
      )}
    </Card>
  );
});

export const EditPlanVestingDialog = observer(function EditPlanVestingDialog({
  edit,
}: {
  edit: EditPlanVestingState;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <Dialog
      title={{ key: "options.vesting-template-for", params: { name: edit.plan.name } }}
      onCancel={[closeEditPlanVestingDialog()]}
      onSubmit={() => dispatch(submitEditPlanVesting(orgNumber, edit))}
      submitResult={edit.submitResult}
      submitLabel={{ key: "save" }}
      isSubmitDisabled={
        isPerformanceVesting(edit.vestingForm) &&
        !performanceMilestonesComplete(edit.grantForm, edit.vestingForm)
      }
    >
      <VBox gap={"lg"} marginY={"md"}>
        <Alert type={"info"} icon message={{ key: "options.vesting-template-example" }} />
        <Grid cols={1} colsMd={2} gap={"lg"} alignItems={"start"}>
          <MTextInput
            label={t("options.number-of-options")}
            model={edit.grantForm.numberOfOptions}
          />
          <MDateInput label={t("instrument.grant-date")} model={edit.grantForm.grantDate} />
        </Grid>
      </VBox>
      <VestingStep
        grantForm={edit.grantForm}
        vestingForm={edit.vestingForm}
        showAll={edit.showAllVestingEntries ?? false}
      />
    </Dialog>
  );
});

export const RegisterCancellationDialog = observer(function RegisterCancellationDialog({
  cancellation,
  tranches,
  today,
}: {
  cancellation: CancellationState;
  tranches: TrancheWithPlan[];
  today: string;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const option = selectedCancellationOption(cancellation);
  const selectedTranche = tranches.find(
    (twp) => twp.tranche.optionTrancheId === cancellation.selectedTrancheId,
  );
  const isCancellationTab = cancellation.activeTab === "cancellation";
  const series = selectedTranche ? prepHolderVestingSeries([selectedTranche]) : [];
  const milestoneGroups = selectedTranche ? prepHolderMilestones([selectedTranche]) : [];
  const stagedAttachments = cancellation.stagedFiles.map((staged) => ({
    id: staged.id,
    filename: staged.file.name,
  }));

  const availableHint = (available: number) => (
    <span className={"subtle pr-2"}>
      {t({ key: "options.of-count", params: { count: { type: "number", value: available } } })}
    </span>
  );

  return (
    <Dialog
      title={{ key: "options.register-cancellation" }}
      maxWidth={"md"}
      onCancel={[closeCancellationDialog()]}
      onSubmit={() => dispatch(submitCancellation(orgNumber, cancellation))}
      submitResult={cancellation.submitResult}
      submitLabel={{ key: "save" }}
      isSubmitDisabled={!cancellationComplete(cancellation)}
    >
      <VBox gap={"lg"} marginY={"sm"}>
        <Select<CancellationGrantOption>
          value={option}
          items={cancellation.grantOptions}
          fullWidth
          placeholder={t("options.select-holder-program")}
          idFn={(item) => item.trancheId}
          labelFn={(item) => ({
            translated: `${item.holderName} – ${item.planName}`,
          })}
          onSelect={(item) => [setCancellationGrant(item.trancheId)]}
        />
        {option === undefined ? (
          <span className={"subtle"}>{t("options.select-holder-program")}</span>
        ) : (
          <>
            <Tabs
              scrollable={false}
              tabs={[
                {
                  id: "cancellation",
                  label: { key: "options.cancellation" },
                  isActive: isCancellationTab,
                  onSelect: [setCancellationTab("cancellation")],
                },
                {
                  id: "schedule",
                  label: { key: "options.schedule-milestones" },
                  isActive: !isCancellationTab,
                  onSelect: [setCancellationTab("schedule")],
                },
              ]}
            />
            {isCancellationTab ? (
              <VBox gap={"lg"}>
                <Grid cols={1} colsMd={2} gap={"lg"} alignItems={"start"}>
                  <MSelect
                    label={t("options.cancel-reason")}
                    model={cancellation.reason}
                    fullWidth
                  />
                  <MDateInput
                    label={t("options.effective-date")}
                    model={cancellation.effectiveDate}
                  />
                  <MTextInput
                    label={t("options.vested-to-cancel")}
                    model={cancellation.vestedToCancel}
                    addon={availableHint(option.vestedAvailable)}
                  />
                  <MTextInput
                    label={t("options.unvested-to-cancel")}
                    model={cancellation.unvestedToCancel}
                    addon={availableHint(option.unvestedAvailable)}
                  />
                </Grid>
                <MTextArea label={t("remarks")} model={cancellation.remarks} rows={4} />
                <VBox gap={"sm"}>
                  <strong>{t("attachments")}</strong>
                  <AttachmentsPanel
                    title={false}
                    fileUploadConfig={optionAttachmentUploadConfig}
                    uploadedAttachments={stagedAttachments}
                    onUpload={(files) => dispatch([stageCancellationFiles(files)])}
                    onDelete={(id) => dispatch([removeStagedCancellationFile(id)])}
                    onOpen={() => undefined}
                    onInvalidFile={(message) => dispatch([showAlert(message, "error")])}
                  />
                </VBox>
              </VBox>
            ) : series.length > 0 || milestoneGroups.length > 0 ? (
              <VBox gap={"md"}>
                {series.length > 0 && (
                  <VestingChart
                    series={series}
                    today={today}
                    exerciseWindows={selectedTranche?.exerciseWindows}
                  />
                )}
                {milestoneGroups.map((group) => (
                  <HolderMilestones key={group.id} group={group} />
                ))}
              </VBox>
            ) : (
              <span className={"subtle"}>{t("options.no-vesting-schedule")}</span>
            )}
          </>
        )}
      </VBox>
    </Dialog>
  );
});

const DeleteGrantDialog = observer(function DeleteGrantDialog({
  deleteGrant,
}: {
  deleteGrant: DeleteGrantState;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  return (
    <Dialog
      maxWidth={"sm"}
      title={{ key: "options.delete-grant-for", params: { name: deleteGrant.holderName } }}
      onCancel={[closeDeleteGrantDialog()]}
      onSubmit={() => dispatch(submitDeleteGrant(orgNumber, deleteGrant))}
      submitResult={deleteGrant.submitResult}
      submitLabel={{ key: "options.delete-grant-confirm" }}
      submitColor={"error"}
    >
      <VBox gap={"md"}>
        <p>
          {t({
            key: "options.delete-grant-description",
            params: {
              program: deleteGrant.program,
              options: { type: "number", value: deleteGrant.totalOptions },
            },
          })}
        </p>
        <p>{t("options.delete-grant-record")}</p>
      </VBox>
    </Dialog>
  );
});

export const RegisterAccelerationDialog = observer(function RegisterAccelerationDialog({
  acceleration,
  tranches,
  today,
}: {
  acceleration: AccelerationState;
  tranches: TrancheWithPlan[];
  today: string;
}) {
  const { orgNumber } = useUserStore();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const option = selectedAccelerationOption(acceleration);
  const selectedTranche = tranches.find(
    (twp) => twp.tranche.optionTrancheId === acceleration.selectedTrancheId,
  );
  const isAccelerationTab = acceleration.activeTab === "acceleration";
  const series = selectedTranche ? prepHolderVestingSeries([selectedTranche]) : [];
  const milestoneGroups = selectedTranche ? prepHolderMilestones([selectedTranche]) : [];
  const stagedAttachments = acceleration.stagedFiles.map((staged) => ({
    id: staged.id,
    filename: staged.file.name,
  }));

  const availableHint = (available: number) => (
    <span className={"subtle pr-2"}>
      {t({ key: "options.of-count", params: { count: { type: "number", value: available } } })}
    </span>
  );

  return (
    <Dialog
      title={
        option === undefined
          ? { key: "options.register-acceleration" }
          : {
              key: "options.register-acceleration-for",
              params: { name: `${option.holderName} – ${option.planName}` },
            }
      }
      maxWidth={"md"}
      onCancel={[closeAccelerationDialog()]}
      onSubmit={() => dispatch(submitAcceleration(orgNumber, acceleration))}
      submitResult={acceleration.submitResult}
      submitLabel={{ key: "save" }}
      isSubmitDisabled={!accelerationComplete(acceleration)}
    >
      <VBox gap={"lg"} marginY={"sm"}>
        {!acceleration.preselected && (
          <Select<AccelerationGrantOption>
            value={option}
            items={acceleration.grantOptions}
            fullWidth
            placeholder={t("options.select-holder-program")}
            idFn={(item) => item.trancheId}
            labelFn={(item) => ({ translated: `${item.holderName} – ${item.planName}` })}
            onSelect={(item) => [setAccelerationGrant(item.trancheId)]}
          />
        )}
        {option === undefined ? (
          <span className={"subtle"}>{t("options.select-holder-program")}</span>
        ) : (
          <>
            <Tabs
              scrollable={false}
              tabs={[
                {
                  id: "acceleration",
                  label: { key: "options.acceleration" },
                  isActive: isAccelerationTab,
                  onSelect: [setAccelerationTab("acceleration")],
                },
                {
                  id: "schedule",
                  label: { key: "options.schedule-milestones" },
                  isActive: !isAccelerationTab,
                  onSelect: [setAccelerationTab("schedule")],
                },
              ]}
            />
            {isAccelerationTab ? (
              <VBox gap={"lg"}>
                <Grid cols={1} colsMd={2} gap={"lg"} alignItems={"start"}>
                  <MTextInput
                    label={t("options.unvested-to-accelerate")}
                    model={acceleration.unvestedToAccelerate}
                    addon={availableHint(option.unvestedAvailable)}
                  />
                  <MDateInput
                    label={t("options.effective-date")}
                    model={acceleration.effectiveDate}
                  />
                </Grid>
                <MTextArea label={t("remarks")} model={acceleration.remarks} rows={4} />
                <VBox gap={"sm"}>
                  <strong>{t("attachments")}</strong>
                  <AttachmentsPanel
                    title={false}
                    fileUploadConfig={optionAttachmentUploadConfig}
                    uploadedAttachments={stagedAttachments}
                    onUpload={(files) => dispatch([stageAccelerationFiles(files)])}
                    onDelete={(id) => dispatch([removeStagedAccelerationFile(id)])}
                    onOpen={() => undefined}
                    onInvalidFile={(message) => dispatch([showAlert(message, "error")])}
                  />
                </VBox>
              </VBox>
            ) : series.length > 0 || milestoneGroups.length > 0 ? (
              <VBox gap={"md"}>
                {series.length > 0 && (
                  <VestingChart
                    series={series}
                    today={today}
                    exerciseWindows={selectedTranche?.exerciseWindows}
                  />
                )}
                {milestoneGroups.map((group) => (
                  <HolderMilestones key={group.id} group={group} />
                ))}
              </VBox>
            ) : (
              <span className={"subtle"}>{t("options.no-vesting-schedule")}</span>
            )}
          </>
        )}
      </VBox>
    </Dialog>
  );
});

const HolderDetailsStep = observer(function HolderDetailsStep({ form }: { form: NewHolderForm }) {
  const { t } = useTranslation();

  return (
    <VBox gap={"xl"} marginY={"md"}>
      <VBox>
        <div>{t("type")}</div>
        <MRadioButtonGroup gap={"xl"} model={form.holderType} horizontal />
      </VBox>
      {form.holderType.value === "PERSON" ? (
        <Grid cols={1} colsMd={2} gap={"lg"}>
          <MSelect
            label={t("options.nationality")}
            model={form.nationality}
            onSelect={(country) => setHolderNationality(form, country)}
            fullWidth
          />
          <MTextInput label={t("options.national-id-number")} model={form.nationalIdNumber} />
          <MTextInput label={t("options.first-name")} model={form.firstName} />
          <MTextInput label={t("options.last-name")} model={form.lastName} />
          <MTextInput label={t("email")} model={form.email} />
          <MTextInput label={t("options.utl-number")} model={form.utlNumber} />
        </Grid>
      ) : (
        <>
          <VBox gap={"md"}>
            <strong>{t("options.company-details")}</strong>
            <Grid cols={1} colsMd={2} gap={"lg"}>
              <MSelect
                label={t("options.nationality")}
                model={form.nationality}
                onSelect={(country) => setHolderNationality(form, country)}
                fullWidth
              />
              <MTextInput label={t("options.company-name")} model={form.companyName} />
              <MTextInput
                label={t("options.organisation-number")}
                model={form.organisationNumber}
              />
              <MTextInput
                label={t("options.utl-number")}
                model={form.utlNumber}
                disabled={form.nationality.value.code === norway}
              />
            </Grid>
          </VBox>
          <VBox gap={"md"}>
            <strong>{t("options.primary-representative")}</strong>
            <Grid cols={1} colsMd={2} gap={"lg"}>
              <MTextInput label={t("options.first-name")} model={form.firstName} />
              <MTextInput label={t("options.last-name")} model={form.lastName} />
              <MTextInput label={t("email")} model={form.email} />
              <MTextInput label={t("options.national-id-number")} model={form.nationalIdNumber} />
            </Grid>
          </VBox>
        </>
      )}
    </VBox>
  );
});
